//
// Group records/definitions by named extended definition (or base definition name).
// Used by Definition Inspector siblings/children menus and extension pickers.
//

const extensionLabel = definition => (definition ? definition.name : null)

/**
 * Group definition objects by extension / base definition name.
 * Use groupExtensionsFromRecords when per-group record counts are needed.
 * @returns {Array<{ key: string, name: string, definitionGid: string, isExtended: boolean }>}
 */
export const groupExtensionsFromDefinitions = definitions => {
    const groups = new Map()

    if (!definitions) return groups

    definitions.forEach(definition => {
        if (!definition) return
        const key = definition.gid
        if (groups.has(key)) return
        groups.set(key, {
            key,
            name: extensionLabel(definition),
            definitionGid: definition.gid,
            isExtended: !!definition.is_extended,
        })
    })
    return Array.from(groups.values()).sort((a, b) => {
        if (a.isExtended !== b.isExtended) return a.isExtended ? -1 : 1
        return a.name.localeCompare(b.name)
    })
}

/**
 * Group records by their definition gid, with counts.
 * @param {Iterable} records
 * @param {{ get: Function }} definitionsStore - data.definitions
 * @returns {Array<{ key: string, name: string, definitionGid: string, isExtended: boolean, count: number }>}
 */
export const groupExtensionsFromRecords = (records, definitionsStore) => {
    const groups = new Map()

    if (!records) return groups

    records.forEach(record => {
        if (!record || !record.definition) return
        const definition = definitionsStore.get(record.definition)
        if (!definition) return
        const key = definition.gid
        if (groups.has(key)) {
            groups.get(key).count += 1
            return
        }
        groups.set(key, {
            key,
            name: extensionLabel(definition),
            definitionGid: definition.gid,
            isExtended: !!definition.is_extended,
            count: 1,
        })
    })
    return Array.from(groups.values()).sort((a, b) => {
        if (a.isExtended !== b.isExtended) return a.isExtended ? -1 : 1
        return a.name.localeCompare(b.name)
    })
}

/**
 * Display label for an extension group (name + optional count).
 */
export const formatExtensionGroupLabel = (group, notExtendedSuffix) => {
    let name = group.name
    if (!group.isExtended && notExtendedSuffix) {
        name = `${name}${notExtendedSuffix}`
    }
    return name
}

/**
 * Group records by base definition, with extensions nested under each base.
 * @returns {Array<{
 *   key: string,
 *   base: { name: string, definitionGid: string, count: number, linkCount: number },
 *   extensions: Array<{ key: string, name: string, definitionGid: string, count: number, hasLink: boolean }>
 * }>}
 */
export const groupExtensionsByBaseFromRecords = (records, definitionsStore) => {
    const families = new Map()

    if (!records) return families

    records.forEach(record => {
        if (!record || !record.definition) return
        const definition = definitionsStore.get(record.definition)
        if (!definition) return

        const baseGid = definition.is_extended ? definition.original : definition.gid
        const baseDefinition =
            definitionsStore.get(baseGid) ||
            (!definition.is_extended ? definition : null)
        if (!baseDefinition) return

        let family = families.get(baseGid)
        if (!family) {
            family = {
                key: baseGid,
                base: {
                    name: extensionLabel(baseDefinition),
                    definitionGid: baseGid,
                    count: 0,
                    linkCount: 0,
                },
                extensions: new Map(),
            }
            families.set(baseGid, family)
        }

        const isLink = !!record.is_link

        if (!definition.is_extended) {
            if (isLink) family.base.linkCount += 1
            else family.base.count += 1
            return
        }

        const extension = family.extensions.get(definition.gid)
        if (extension) {
            extension.count += 1
            if (isLink) extension.hasLink = true
            return
        }
        family.extensions.set(definition.gid, {
            key: definition.gid,
            name: extensionLabel(definition),
            definitionGid: definition.gid,
            count: 1,
            hasLink: isLink,
        })
    })

    return Array.from(families.values())
        .map(family => ({
            key: family.key,
            base: family.base,
            extensions: Array.from(family.extensions.values()).sort((a, b) =>
                a.name.localeCompare(b.name)
            ),
        }))
        .sort((a, b) => a.base.name.localeCompare(b.base.name))
}

/**
 * Group definition objects by base definition, with extensions nested under each base.
 * @param {Iterable} definitions
 * @param {{ get: Function }} definitionsStore
 * @param {{ linkDefinitionGids?: Set<string> }} [options] - definition gids used by linked records
 * @returns {Array<{
 *   key: string,
 *   base: { name: string, definitionGid: string, hasLink: boolean },
 *   extensions: Array<{ key: string, name: string, definitionGid: string, hasLink: boolean }>
 * }>}
 */
export const groupExtensionsByBaseFromDefinitions = (
    definitions,
    definitionsStore,
    { linkDefinitionGids } = {}
) => {
    const families = new Map()

    if (!definitions) return families

    definitions.forEach(definition => {
        if (!definition) return

        const baseGid = definition.is_extended ? definition.original : definition.gid
        const baseDefinition =
            definitionsStore.get(baseGid) ||
            (!definition.is_extended ? definition : null)
        if (!baseDefinition) return

        let family = families.get(baseGid)
        if (!family) {
            family = {
                key: baseGid,
                base: {
                    name: extensionLabel(baseDefinition),
                    definitionGid: baseGid,
                    hasLink: !!(linkDefinitionGids && linkDefinitionGids.has(baseGid)),
                },
                extensions: new Map(),
            }
            families.set(baseGid, family)
        }

        if (!definition.is_extended) {
            if (linkDefinitionGids && linkDefinitionGids.has(definition.gid)) {
                family.base.hasLink = true
            }
            return
        }

        if (!family.extensions.has(definition.gid)) {
            family.extensions.set(definition.gid, {
                key: definition.gid,
                name: extensionLabel(definition),
                definitionGid: definition.gid,
                hasLink: !!(
                    linkDefinitionGids && linkDefinitionGids.has(definition.gid)
                ),
            })
        } else if (linkDefinitionGids && linkDefinitionGids.has(definition.gid)) {
            family.extensions.get(definition.gid).hasLink = true
        }
    })

    return Array.from(families.values())
        .map(family => ({
            key: family.key,
            base: family.base,
            extensions: Array.from(family.extensions.values()).sort((a, b) =>
                a.name.localeCompare(b.name)
            ),
        }))
        .sort((a, b) => a.base.name.localeCompare(b.base.name))
}
